'use client'
import { useState, useEffect } from 'react'
import { useCart } from '@/context/CartContext'
import { calcTotal, calcBreakdown } from '@/lib/pricing'
import { validateName, validatePhone, isValidQty, QTY_MAX } from '@/lib/validation'
import { IconTrash, IconPlus, IconMinus, IconCheck } from '@/icons'
import PhoneInput from '@/components/PhoneInput'
import { postApi } from '@/lib/api'
import { deployPath } from '@/lib/site'

function fmt(n) { return n.toLocaleString('ru') + ' ₽' }

export default function CartModal() {
  const { items, cartOpen, openCart, closeCart, remove, setQty, clear } = useCart()
  const [form, setForm]   = useState({ name: '', phone: '' })
  const [errs, setErrs]   = useState({})
  const [sent, setSent]   = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!cartOpen) return
    const onKey = (e) => { if (e.key === 'Escape') closeCart() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [cartOpen, closeCart])

  const hasNullPrice = items.some(x => calcTotal(x) === null)
  const total = items.reduce((s, x) => {
    const t = calcTotal(x)
    return t !== null ? s + t : s
  }, 0)
  const totalStr = hasNullPrice
    ? (total > 0 ? fmt(total) + ' + по запросу' : 'по запросу')
    : fmt(total)

  const setField = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }))
    setErrs(er => ({ ...er, [k]: undefined }))
  }

  const submit = async (e) => {
    e.preventDefault()
    const next = {}
    const nameErr  = validateName(form.name)
    const phoneErr = validatePhone(form.phone)
    if (nameErr)  next.name  = nameErr
    if (phoneErr) next.phone = phoneErr
    setErrs(next)
    if (Object.keys(next).length > 0) return

    setLoading(true)
    setErrs(er => ({ ...er, submit: undefined }))
    try {
      await postApi('/api/order', { name: form.name, phone: form.phone, items })
      setSent(true)
      clear()
      setForm({ name: '', phone: '' })
      setTimeout(() => { setSent(false); closeCart() }, 3000)
    } catch (error) {
      setErrs(er => ({ ...er, submit: error.message }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    {items.length > 0 && !cartOpen && (
      <button className="cart-fab" onClick={openCart} aria-label="Открыть корзину">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
        </svg>
        <span>Оформить заказ</span>
        <span className="cart-fab-badge">{items.reduce((s, x) => s + x.qty, 0)}</span>
      </button>
    )}
    {cartOpen && <div className="modal-backdrop" onMouseDown={closeCart}>
      <div className="modal-card cart-modal-card" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cart-modal-title">

        <div className="modal-hd">
          <div>
            <h2 className="modal-title" id="cart-modal-title">Корзина</h2>
            {items.length > 0 && (
              <p className="modal-desc">
                {items.length} поз. · {items.reduce((s, x) => s + x.qty, 0)} шт.
              </p>
            )}
          </div>
          <button className="modal-close" onClick={closeCart} aria-label="Закрыть">✕</button>
        </div>

        <div className="modal-body">
          {items.length === 0 ? (
            sent ? (
              <div className="cart-sent">
                <IconCheck width="32" height="32" />
                <p>Заказ оформлен — мы свяжемся с вами в ближайшее время</p>
              </div>
            ) : (
              <div className="cart-empty">
                <p>Корзина пуста</p>
                <span>Добавьте товары из раздела «Продукция»</span>
              </div>
            )
          ) : (
            <>
              <ul className="cart-items">
                {items.map(item => {
                  const total     = calcTotal(item)
                  const breakdown = calcBreakdown(item)

                  return (
                    <li key={item.id} className="cart-item">
                      <div className="cart-item-info">
                        <span className="cart-item-name">{item.name}</span>
                        <span className="cart-item-label">{item.label}</span>
                        {breakdown && (
                          <span className="cart-item-breakdown">{breakdown}</span>
                        )}
                      </div>
                      <div className="cart-item-controls">
                        <div className="qty-ctrl">
                          <button onClick={() => setQty(item.id, item.qty - 1)} aria-label="Меньше">
                            <IconMinus width="12" height="12" />
                          </button>
                          <input
                            type="number" min="1" max={QTY_MAX} value={item.qty}
                            onChange={e => {
                              const n = parseInt(e.target.value, 10)
                              if (isValidQty(n)) setQty(item.id, n)
                            }}
                            className="cart-qty-input"
                          />
                          <button onClick={() => setQty(item.id, item.qty + 1)} aria-label="Больше">
                            <IconPlus width="12" height="12" />
                          </button>
                        </div>
                        <span className="cart-item-price">
                          {total !== null ? fmt(total) : 'по запросу'}
                        </span>
                        <button className="cart-remove" onClick={() => remove(item.id)} aria-label="Удалить">
                          <IconTrash width="14" height="14" />
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>

              <div className="cart-total">
                <span>Итого</span>
                <span className="cart-total-val">{totalStr}</span>
              </div>

              <form className="cart-form" onSubmit={submit} noValidate>
                <div className="cart-form-row">
                  <div className={`field ${errs.name ? 'err' : ''}`}>
                    <label className="sr-only" htmlFor="cart-name">Ваше имя</label>
                    <input id="cart-name" name="name" type="text" placeholder="Ваше имя" value={form.name} onChange={setField('name')} />
                    {errs.name && <div className="em">{errs.name}</div>}
                  </div>
                  <div className={`field ${errs.phone ? 'err' : ''}`}>
                    <label className="sr-only" htmlFor="cart-phone">Телефон</label>
                    <PhoneInput id="cart-phone" name="phone" aria-label="Телефон" value={form.phone} onChange={setField('phone')} />
                    {errs.phone && <div className="em">{errs.phone}</div>}
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                  {loading ? 'Отправка...' : 'Оформить заказ'}
                </button>
                {errs.submit && <div className="em">{errs.submit}</div>}
                <p className="consent-text">
                  Нажимая кнопку «Оформить заказ», я даю согласие на обработку моих персональных данных в соответствии с&nbsp;
                  <a href={deployPath('/policy')} target="_blank" rel="noopener noreferrer" className="consent-link">Политикой конфиденциальности</a>
                  &nbsp;и Федеральным законом №&nbsp;152-ФЗ «О персональных данных».
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>}
    </>
  )
}
